/*
* Frame Anything
* @param {object} opts
*/

import Flickity from 'flickity';

function frameAnything(opts) {
  const config = {
    dom: {
      section: 'js-frame-anything',
      carousel: 'js-frame-anything-carousel',
      prevBtn: 'js-frame-anything-prev',
      nextBtn: 'js-frame-anything-next',
      dot: 'js-frame-anything-dot',
      playPauseBtn: 'js-frame-anything-play-pause-btn',
    },
    cls: {
      playing: 'playing',
      selected: 'is-selected',
    },
    flickityOpts: {
      cellAlign: 'left',
      draggable: true,
      freeScroll: true,
      pageDots: false,
      prevNextButtons: false,
      setGallerySize: false,
      wrapAround: true,
    },
  };

  const c = __.extend(config, opts);
  const $sections = document.querySelectorAll(`.${c.dom.section}`);

  /**
   * Init
   */
  function init() {
    if (!$sections?.length) return;
    $sections.forEach(($section) => setupSection($section));
  }

  /**
   * Set up a section: Flickity carousel, autoplay marquee and custom controls
   * @param {Element} $section
   */
  function setupSection($section) {
    const $carousel = $section.querySelector(`.${c.dom.carousel}`);
    if (!$carousel) return;

    const $prevBtn = $section.querySelector(`.${c.dom.prevBtn}`);
    const $nextBtn = $section.querySelector(`.${c.dom.nextBtn}`);
    const $dots = [...$section.querySelectorAll(`.${c.dom.dot}`)];
    const $playPauseBtn = $section.querySelector(`.${c.dom.playPauseBtn}`);
    const rotationSpeed = parseInt($carousel.dataset.rotationSpeed, 10) || 50;
    const repeats = parseInt($carousel.dataset.repeats, 10) || 1;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canHover = window.matchMedia('(hover: hover)').matches;

    const flickity = new Flickity($carousel, c.flickityOpts);

    let isPlaying = !prefersReducedMotion;
    let isHovered = false;
    let rafId = null;
    let lastFrameTime = null;
    let activeDot = 0;

    /**
     * Advance the marquee; speed matches the previous CSS ticker where one
     * set of blocks scrolls past in rotationSpeed seconds
     */
    function tick(timestamp) {
      if (lastFrameTime !== null) {
        const elapsedSeconds = (timestamp - lastFrameTime) / 1000;
        const pxPerSecond = flickity.slideableWidth / repeats / rotationSpeed;

        flickity.x = (flickity.x - pxPerSecond * elapsedSeconds) % flickity.slideableWidth;
        flickity.settle(flickity.x);
      }

      lastFrameTime = timestamp;
      rafId = window.requestAnimationFrame(tick);
    }

    function play() {
      if (rafId || isHovered || !isPlaying || flickity.isAnimating) return;
      lastFrameTime = null;
      rafId = window.requestAnimationFrame(tick);
    }

    function pause() {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }

    /**
     * Index of the slide closest to the current track position; may equal
     * slides.length when the closest slide is the wrapped-around first one
     */
    function nearestSlideIndex() {
      const slidesCount = flickity.slides.length;
      let closestIndex = flickity.selectedIndex;
      let closestDistance = Infinity;

      for (let index = 0; index <= slidesCount; index += 1) {
        const distance = flickity.getSlideDistance(-flickity.x, index);

        if (distance !== null && Math.abs(distance) < closestDistance) {
          closestDistance = Math.abs(distance);
          closestIndex = index;
        }
      }

      return closestIndex;
    }

    /**
     * Align the selected slide with the marquee position so arrows move
     * exactly one tile from where the track currently sits
     */
    function syncSelectedSlide() {
      let closestIndex = nearestSlideIndex();

      if (closestIndex >= flickity.slides.length) {
        flickity.x += flickity.slideableWidth;
        closestIndex -= flickity.slides.length;
      }

      flickity.selectedIndex = closestIndex;
      flickity.updateSelectedSlide();
    }

    /**
    * Highlight the dot for the block nearest the track position; duplicated
    * cells map back to their original block via modulo
    */
    function updateDots() {
      if (!$dots.length) return;

      const index = nearestSlideIndex() % $dots.length;
      if (index === activeDot) return;

      __.removeClass($dots[activeDot], c.cls.selected);
      __.addClass($dots[index], c.cls.selected);
      activeDot = index;
    }

    /**
    * @param {string} direction - 'previous' or 'next'
    */
    function onArrowClick(direction) {
      pause();
      syncSelectedSlide();
      flickity[direction]();
    }

    /**
    * Select the cell for a dot, preferring the duplicate closest to the
    * current track position
    * @param {number} dotIndex
    */
    function onDotClick(dotIndex) {
      pause();
      syncSelectedSlide();

      let target = dotIndex;
      let closestDistance = Infinity;

      for (let index = dotIndex; index < flickity.slides.length; index += $dots.length) {
        const distance = Math.abs(flickity.getSlideDistance(-flickity.x, index));

        if (distance < closestDistance) {
          closestDistance = distance;
          target = index;
        }
      }

      flickity.select(target);
    }

    /**
    * Hold the marquee still while the pointer is over the carousel so tiles
    * can be hovered and clicked
    * @param {boolean} hovered
    */
    function onHoverChange(hovered) {
      isHovered = hovered;

      if (hovered) {
        pause();
      } else {
        play();
      }
    }

    /**
    * Toggle play/pause
    */
    function togglePlayPause() {
      isPlaying = !isPlaying;

      if (isPlaying) {
        __.addClass($playPauseBtn, c.cls.playing);
        play();
      } else {
        __.removeClass($playPauseBtn, c.cls.playing);
        pause();
      }
    }

    flickity.on('pointerDown', pause);
    flickity.on('pointerUp', () => {
      if (!flickity.isDragging) play();
    });
    flickity.on('uiChange', () => {
      pause();
      syncSelectedSlide();
    });
    flickity.on('settle', play);
    flickity.on('scroll', updateDots);

    __.addEvent({
      id: $prevBtn,
      event: 'click',
      fn: () => onArrowClick('previous'),
    });

    __.addEvent({
      id: $nextBtn,
      event: 'click',
      fn: () => onArrowClick('next'),
    });

    $dots.forEach(($dot, index) => {
      __.addEvent({
        id: $dot,
        event: 'click',
        fn: () => onDotClick(index),
      });
    });

    __.addEvent({
      id: $playPauseBtn,
      event: 'click',
      fn: togglePlayPause,
    });

    if (canHover) {
      __.addEvent({
        id: $carousel,
        event: 'mouseenter',
        fn: () => onHoverChange(true),
      });

      __.addEvent({
        id: $carousel,
        event: 'mouseleave',
        fn: () => onHoverChange(false),
      });
    }

    if (!isPlaying) __.removeClass($playPauseBtn, c.cls.playing);

    play();
  }

  return init();
}

export default frameAnything;
